import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MapPin, CheckCircle, X, Phone, Mail, Search, Loader2, List, Map as MapIcon } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ServiceCategory } from './BookingFlow';
import { apiClient } from '../../../../api/axios';

// Fix Leaflet default marker icon (missing in bundled builds)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom purple marker for Petso professionals
const petsoMarkerIcon = new L.DivIcon({
  className: '',
  html: `<div style="background:#6C5CE7;width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);font-size:14px">🏥</span></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Imperative Leaflet map component — avoids react-leaflet rendering issues
function LeafletMap({ positions, businesses, onBookNow, getLocationString }: {
  positions: Record<string, [number, number]>;
  businesses: Business[];
  onBookNow: (biz: Business) => void;
  getLocationString: (biz: Business) => string;
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const posEntries = Object.entries(positions);
    const center: L.LatLngExpression = posEntries.length > 0
      ? [posEntries[0][1][0], posEntries[0][1][1]]
      : [52.0, 5.0];
    const zoom = posEntries.length > 0 ? 13 : 5;

    const map = L.map(mapRef.current, {
      center,
      zoom,
      scrollWheelZoom: true,
      dragging: true,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(map);

    // Add markers
    for (const biz of businesses) {
      const pos = positions[biz.id];
      if (!pos) continue;
      const marker = L.marker(pos, { icon: petsoMarkerIcon }).addTo(map);
      marker.bindPopup(`
        <div style="min-width:160px">
          <p style="font-weight:700;font-size:13px;margin:0 0 4px">${biz.name}</p>
          <p style="font-size:11px;color:#888;margin:0 0 8px">${getLocationString(biz)}</p>
          <button onclick="window.__petsoBookNow__('${biz.id}')" style="width:100%;padding:6px;background:#4F46E5;color:white;border:none;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer">Book Now</button>
        </div>
      `);
    }

    // Fit bounds if multiple markers
    if (posEntries.length > 1) {
      const bounds = L.latLngBounds(posEntries.map(([, pos]) => [pos[0], pos[1]]));
      map.fitBounds(bounds, { padding: [30, 30] });
    }

    mapInstanceRef.current = map;

    // Force size recalculation
    setTimeout(() => map.invalidateSize(), 200);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [positions, businesses, getLocationString]);

  // Global callback for popup buttons
  useEffect(() => {
    (window as any).__petsoBookNow__ = (bizId: string) => {
      const biz = businesses.find(b => b.id === bizId);
      if (biz) onBookNow(biz);
    };
    return () => { delete (window as any).__petsoBookNow__; };
  }, [businesses, onBookNow]);

  return <div ref={mapRef} style={{ width: '100%', height: '100%' }} />;
}

interface Business {
  id: string;
  name: string;
  businessTypes: string[] | null;
  email: string | null;
  phone: string | null;
  logoUrl: string | null;
  description: string | null;
  address: { street?: string; city?: string; state?: string; zipCode?: string; country?: string } | null;
  serviceArea: string | null;
  website: string | null;
}

interface ProfessionalDirectoryProps {
  category: ServiceCategory;
  selectedProfessional: any;
  selectedServices?: string[];
  onProfessionalSelect: (professional: any) => void;
  onNext?: () => void;
}

const CATEGORY_TO_TYPE: Record<string, string> = {
  veterinary: 'veterinary', grooming: 'grooming', boarding: 'boarding', training: 'training',
  walking: 'walking', breeding: 'breeding', retail: 'retail', nutrition: 'nutrition',
  transportation: 'transportation', insurance: 'insurance', identification: 'identification',
  endoflife: 'endoflife', rescue: 'rescue', events: 'events', education: 'education',
};

const CATEGORY_ICON: Record<string, string> = {
  veterinary: '\u{1F3E5}', grooming: '\u{2702}\u{FE0F}', boarding: '\u{1F3E8}', training: '\u{1F3AF}',
  walking: '\u{1F6B6}', breeding: '\u{1F43E}', retail: '\u{1F6D2}', nutrition: '\u{1F957}',
  transportation: '\u{1F698}', insurance: '\u{1F6E1}\u{FE0F}', identification: '\u{1F4CB}',
  endoflife: '\u{1F54A}\u{FE0F}', rescue: '\u{2764}\u{FE0F}', events: '\u{1F4F8}', education: '\u{1F393}',
};

const CATEGORY_LABEL: Record<string, string> = {
  veterinary: 'Veterinarian', grooming: 'Groomer', boarding: 'Boarding Facility', training: 'Trainer',
  walking: 'Pet Walker', breeding: 'Breeder', retail: 'Pet Shop', nutrition: 'Nutritionist',
  transportation: 'Transport Service', insurance: 'Insurance Provider', identification: 'ID Service',
  endoflife: 'Memorial Service', rescue: 'Rescue Organisation', events: 'Events', education: 'Education Centre',
};

// Haversine distance in km between two lat/lng points
function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function ProfessionalDirectory({ category, selectedProfessional, selectedServices, onProfessionalSelect, onNext }: ProfessionalDirectoryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [activeLocation, setActiveLocation] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState<string[]>([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [allBusinesses, setAllBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewingProfile, setViewingProfile] = useState<Business | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const locationContainerRef = useRef<HTMLDivElement>(null);
  const locationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Geocoded positions for map markers and distance calculations
  const [geocodedPositions, setGeocodedPositions] = useState<Record<string, [number, number]>>({});
  // User's search location coordinates
  const [searchCoords, setSearchCoords] = useState<[number, number] | null>(null);
  // Distance from search location per business
  const [distances, setDistances] = useState<Record<string, number>>({});
  const [geolocating, setGeolocating] = useState(false);

  // Use browser geolocation to get current position
  const useMyLocation = useCallback(async () => {
    if (!navigator.geolocation) return;
    setGeolocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        setSearchCoords([lat, lon]);
        // Reverse geocode to get a human-readable name
        try {
          const resp = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&zoom=14`,
            { headers: { 'Accept-Language': 'en' } }
          );
          if (resp.ok) {
            const data = await resp.json();
            const addr = data.address ?? {};
            const city = addr.city || addr.town || addr.village || addr.municipality || '';
            const country = addr.country || '';
            const label = [city, country].filter(Boolean).join(', ') || data.display_name?.split(',').slice(0, 2).join(',') || 'My location';
            setLocationQuery(label);
            setActiveLocation(city || label.split(',')[0]);
          }
        } catch { /* ignore */ }
        setGeolocating(false);
      },
      () => { setGeolocating(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // Filter + sort businesses: within 50km if location set, sorted by distance
  const businesses = useMemo(() => {
    let filtered = allBusinesses;
    // Name filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(b => b.name.toLowerCase().includes(q));
    }
    // Distance filter + sort when we have search coords and geocoded positions
    if (searchCoords && Object.keys(geocodedPositions).length > 0) {
      const withDist = filtered.map(b => {
        const pos = geocodedPositions[b.id];
        const dist = pos ? haversineKm(searchCoords[0], searchCoords[1], pos[0], pos[1]) : Infinity;
        return { biz: b, dist };
      });
      // Filter within 50km radius
      const nearby = withDist.filter(x => x.dist <= 50);
      // Sort nearest first
      nearby.sort((a, b) => a.dist - b.dist);
      return nearby.map(x => x.biz);
    }
    return filtered;
  }, [allBusinesses, searchQuery, searchCoords, geocodedPositions]);

  // Compute distances separately (can't setState inside useMemo)
  useEffect(() => {
    if (!searchCoords || Object.keys(geocodedPositions).length === 0) {
      setDistances({});
      return;
    }
    const newDist: Record<string, number> = {};
    for (const biz of allBusinesses) {
      const pos = geocodedPositions[biz.id];
      if (pos) newDist[biz.id] = haversineKm(searchCoords[0], searchCoords[1], pos[0], pos[1]);
    }
    setDistances(newDist);
  }, [searchCoords, geocodedPositions, allBusinesses]);

  // Geocode business addresses for distance calculation + map pins
  useEffect(() => {
    if (allBusinesses.length === 0) return;
    let cancelled = false;
    const geocode = async () => {
      const positions: Record<string, [number, number]> = {};
      for (const biz of allBusinesses) {
        if (!biz.address?.city) continue;
        // Build free-form query prioritizing postcode for accurate geocoding
        // Structured queries ignore postcodes in some countries (e.g. Netherlands)
        const q = biz.address.zipCode
          ? [biz.address.street, biz.address.zipCode, biz.address.country].filter(Boolean).join(', ')
          : [biz.address.street, biz.address.city, biz.address.country].filter(Boolean).join(', ');
        try {
          const resp = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1`, { headers: { 'Accept-Language': 'en' } });
          if (!resp.ok) continue;
          const results = await resp.json();
          if (results.length > 0 && !cancelled) {
            positions[biz.id] = [parseFloat(results[0].lat), parseFloat(results[0].lon)];
          }
        } catch { /* ignore */ }
      }
      if (!cancelled) setGeocodedPositions(positions);
    };
    geocode();
    return () => { cancelled = true; };
  }, [allBusinesses]);

  // (Map center/zoom handled inside LeafletMap component)

  // Close location dropdown on outside click — delay to avoid race with suggestion clicks
  useEffect(() => {
    if (!locationDropdownOpen) return;
    let mounted = true;
    const close = (e: MouseEvent) => {
      // Delay check so suggestion onClick has time to fire first
      setTimeout(() => {
        if (!mounted) return;
        if (locationContainerRef.current && !locationContainerRef.current.contains(e.target as Node)) {
          setLocationDropdownOpen(false);
        }
      }, 150);
    };
    document.addEventListener('mousedown', close);
    return () => { mounted = false; document.removeEventListener('mousedown', close); };
  }, [locationDropdownOpen]);

  // Fetch ALL professionals of this type from Petso API (filtering by distance is done client-side)
  const fetchBusinesses = useCallback(async () => {
    setIsLoading(true);
    try {
      const type = CATEGORY_TO_TYPE[category] || category;
      const { data } = await apiClient.get<{ businesses: Business[] }>('/professionals/search', { params: { type } });
      setAllBusinesses(data.businesses ?? []);
    } catch {
      setAllBusinesses([]);
    } finally {
      setIsLoading(false);
    }
  }, [category]);

  useEffect(() => { fetchBusinesses(); }, [fetchBusinesses]);

  // Nominatim location autocomplete (same as CreatePost "Add Location")
  const searchLocations = useCallback(async (query: string) => {
    if (!query.trim()) {
      setLocationSuggestions([]);
      return;
    }
    setLocationLoading(true);
    try {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      if (!resp.ok) return;
      const results: Array<{ display_name: string; name: string; address: Record<string, string> }> = await resp.json();
      const places = results.map((r) => {
        const addr = r.address ?? {};
        const parts: string[] = [];
        const firstName = r.name || r.display_name.split(',')[0].trim();
        parts.push(firstName);
        const city = addr.city ?? addr.town ?? addr.village ?? '';
        if (city && !firstName.includes(city)) parts.push(city);
        if (addr.country) parts.push(addr.country);
        return [...new Set(parts)].join(', ');
      });
      setLocationSuggestions([...new Set(places)].filter(Boolean));
    } catch {
      // ignore
    } finally {
      setLocationLoading(false);
    }
  }, []);

  const handleLocationInput = (val: string) => {
    setLocationQuery(val);
    setLocationDropdownOpen(true);
    if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);
    locationDebounceRef.current = setTimeout(() => searchLocations(val), 300);
  };

  const selectLocation = async (place: string) => {
    setLocationQuery(place);
    setLocationDropdownOpen(false);
    setLocationSuggestions([]);
    const city = place.split(',')[0].trim();
    setActiveLocation(city);
    // Geocode the selected place to get lat/lng for distance filtering
    try {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(place)}&format=json&limit=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      if (resp.ok) {
        const results = await resp.json();
        if (results.length > 0) {
          setSearchCoords([parseFloat(results[0].lat), parseFloat(results[0].lon)]);
        }
      }
    } catch { /* ignore */ }
  };

  const clearLocation = () => {
    setLocationQuery('');
    setActiveLocation('');
    setLocationSuggestions([]);
    setSearchCoords(null);
    setDistances({});
  };

  const handleBookNow = (business: Business) => {
    onProfessionalSelect({
      id: business.id, name: business.name, email: business.email,
      phone: business.phone, logoUrl: business.logoUrl,
      businessTypes: business.businessTypes, address: business.address,
    });
    if (onNext) onNext();
  };

  const icon = CATEGORY_ICON[category] || '\u{1F3E0}';
  const label = CATEGORY_LABEL[category] || 'Professional';

  const getBusinessLocationString = (biz: Business) => {
    if (!biz.address) return '';
    return [biz.address.city, biz.address.zipCode, biz.address.country].filter(Boolean).join(', ');
  };

  // (Static map removed — using interactive Leaflet map now)

  return (
    <div className="space-y-4">
      {/* Service Needed Header */}
      {selectedServices && selectedServices.length > 0 && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-xl">
          <h4 className="text-xs uppercase tracking-wide text-blue-800 dark:text-blue-300 mb-1.5" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
            Service Needed
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {selectedServices.includes('all') ? (
              <span className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm font-semibold">All Services</span>
            ) : (
              selectedServices.map((service, index) => (
                <span key={index} className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm font-semibold">{service}</span>
              ))
            )}
          </div>
        </div>
      )}

      {/* Header with view toggle */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>Find {label}</h3>
          <p className="text-sm text-gray-500 dark:text-white/60">
            {isLoading ? 'Searching...' : `${businesses.length} professional${businesses.length !== 1 ? 's' : ''} found`}
          </p>
        </div>
        <div className="flex bg-gray-100 dark:bg-white/10 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              viewMode === 'list' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-white/50'
            }`}
          >
            <List className="w-3.5 h-3.5" /> List
          </button>
          <button
            onClick={() => setViewMode('map')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              viewMode === 'map' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-white/50'
            }`}
          >
            <MapIcon className="w-3.5 h-3.5" /> Map
          </button>
        </div>
      </div>

      {/* Search Filters */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={`Search ${label.toLowerCase()}s by name...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Location autocomplete (Nominatim — same as CreatePost "Add Location") */}
        <div ref={locationContainerRef} className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
          <input
            type="text"
            placeholder="City, postcode or address..."
            value={locationQuery}
            onChange={(e) => handleLocationInput(e.target.value)}
            onFocus={() => { if (locationSuggestions.length > 0) setLocationDropdownOpen(true); }}
            onBlur={() => { setTimeout(() => setLocationDropdownOpen(false), 200); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setLocationDropdownOpen(false);
                if (locationQuery.trim()) selectLocation(locationQuery);
              }
            }}
            className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {(locationQuery || activeLocation) ? (
            <button onClick={clearLocation} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 z-10">
              <X className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={useMyLocation}
              disabled={geolocating}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors z-10 disabled:opacity-50"
              title="Use my current location"
            >
              {geolocating ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4M2 12h4m12 0h4"/></svg>
                  Nearby
                </>
              )}
            </button>
          )}

          {/* Location suggestions dropdown — use onMouseDown to fire before blur closes it */}
          {locationDropdownOpen && (locationSuggestions.length > 0 || locationLoading) && (
            <div
              className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-[#16151D] border border-gray-200 dark:border-white/20 rounded-xl shadow-lg overflow-hidden"
              style={{ zIndex: 9999 }}
              onMouseDown={(e) => e.preventDefault()}
            >
              {locationLoading ? (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500 dark:text-white/50">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Searching places...
                </div>
              ) : (
                locationSuggestions.map((place, i) => (
                  <button
                    key={i}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); selectLocation(place); }}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-white/10 flex items-center gap-2 text-gray-700 dark:text-white/90 border-b border-gray-100 dark:border-white/8 last:border-0 transition-colors cursor-pointer"
                  >
                    <MapPin className="w-4 h-4 text-purple-500 shrink-0" />
                    {place}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {activeLocation && (
          <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
            <MapPin className="w-3 h-3" />
            <span>Within 50 km of {activeLocation}</span>
            <button onClick={clearLocation} className="ml-1 text-gray-400 hover:text-red-500">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Map View — interactive Leaflet map with Petso-registered professionals */}
      {viewMode === 'map' && (
        <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-white/10">
          {businesses.length > 0 ? (
            <>
              <div className="relative w-full bg-gray-100 dark:bg-gray-800" style={{ height: 350 }}>
                <LeafletMap
                  positions={geocodedPositions}
                  businesses={businesses}
                  onBookNow={handleBookNow}
                  getLocationString={getBusinessLocationString}
                />
              </div>
              <div className="p-3 bg-gray-50 dark:bg-white/5 border-t border-gray-200 dark:border-white/10">
                <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-white/40 mb-2 font-semibold">
                  Petso Registered Professionals ({businesses.length})
                </p>
                <div className="space-y-2">
                  {businesses.map(biz => (
                    <button
                      key={biz.id}
                      onClick={() => handleBookNow(biz)}
                      className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg border text-left transition-all ${
                        selectedProfessional?.id === biz.id
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 hover:border-blue-400'
                      }`}
                    >
                      <span className="text-xl">{icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-sm text-gray-800 dark:text-white truncate">{biz.name}</span>
                          <CheckCircle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                        </div>
                        {biz.address?.city && (
                          <div className="text-[11px] text-gray-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {getBusinessLocationString(biz)}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                        {distances[biz.id] !== undefined && (
                          <span className="text-[10px] text-gray-400">{distances[biz.id] < 1 ? `${Math.round(distances[biz.id] * 1000)}m` : `${distances[biz.id].toFixed(1)} km`}</span>
                        )}
                        <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{'\u2192'} Book</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="p-8 text-center">
              <div className="text-4xl mb-3">{icon}</div>
              <p className="text-sm text-gray-500 dark:text-white/50">
                {isLoading ? 'Searching...' : 'No Petso-registered professionals found in this area.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Loading */}
      {isLoading && viewMode === 'list' && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
          <span className="ml-2 text-sm text-gray-500 dark:text-white/60">Searching professionals...</span>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && businesses.length === 0 && viewMode === 'list' && (
        <div className="text-center py-10 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-200 dark:border-white/10">
          <div className="text-4xl mb-3">{icon}</div>
          <h4 className="text-base font-semibold text-gray-700 dark:text-white/80 mb-1">No professionals found</h4>
          <p className="text-sm text-gray-500 dark:text-white/50">
            {activeLocation ? 'Try a different location or clear the filter.' : searchQuery ? 'Try a different search term.' : `No ${label.toLowerCase()}s are registered on Petso yet.`}
          </p>
        </div>
      )}

      {/* Professional Cards (list view) */}
      {!isLoading && viewMode === 'list' && (
        <div className="space-y-3">
          {businesses.map(business => (
            <div
              key={business.id}
              className={`bg-white dark:bg-[#16151D] border-2 rounded-xl p-4 cursor-pointer transition-all ${
                selectedProfessional?.id === business.id
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500 shadow-lg'
                  : 'border-gray-200 dark:border-white/15 hover:border-gray-300 dark:hover:border-white/25 hover:shadow-md'
              }`}
              onClick={() => onProfessionalSelect({
                id: business.id, name: business.name, email: business.email,
                phone: business.phone, logoUrl: business.logoUrl, businessTypes: business.businessTypes, address: business.address,
              })}
            >
              <div className="flex gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/40 dark:to-purple-900/40 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {business.logoUrl ? (
                    <img src={business.logoUrl} alt={business.name} className="w-full h-full object-cover rounded-xl" />
                  ) : (
                    <span className="text-2xl">{icon}</span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <h4 className="text-sm font-bold dark:text-white truncate">{business.name}</h4>
                        <CheckCircle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                      </div>
                      {business.businessTypes && business.businessTypes.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-1.5">
                          {business.businessTypes.slice(0, 3).map((t, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-gray-100 dark:bg-white/10 dark:text-white/70 rounded text-[10px] capitalize">
                              {t.replace(/_/g, ' ')}
                            </span>
                          ))}
                          {business.businessTypes.length > 3 && (
                            <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-white/10 text-gray-400 rounded text-[10px]">
                              +{business.businessTypes.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-400 flex-shrink-0">
                      Available
                    </span>
                  </div>

                  {business.description && (
                    <p className="text-xs text-gray-500 dark:text-white/50 mb-1.5 line-clamp-1">{business.description}</p>
                  )}

                  <div className="flex items-center gap-3 text-[11px] text-gray-500 dark:text-white/50 mb-2 flex-wrap">
                    {business.address?.city && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        <span>{getBusinessLocationString(business)}</span>
                      </div>
                    )}
                    {business.phone && (
                      <div className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        <span>{business.phone}</span>
                      </div>
                    )}
                    {business.email && (
                      <div className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        <span className="truncate max-w-[150px]">{business.email}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); setViewingProfile(business); }}
                      className="px-3 py-1.5 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 dark:text-white text-xs"
                    >
                      View Profile
                    </button>
                    {distances[business.id] !== undefined && (
                      <span className="text-[11px] text-gray-400 dark:text-white/40 font-medium">
                        {distances[business.id] < 1
                          ? `${Math.round(distances[business.id] * 1000)}m`
                          : `${distances[business.id].toFixed(1)} km`}
                      </span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleBookNow(business); }}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {onNext && (
        <div className="flex justify-end mt-4">
          <button onClick={onNext} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
            Next
          </button>
        </div>
      )}

      {/* Professional Profile Modal */}
      {viewingProfile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#16151D] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-[#16151D] border-b border-gray-200 dark:border-white/8 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>Professional Profile</h3>
              <button onClick={() => setViewingProfile(null)} className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors">
                <X className="w-5 h-5 dark:text-white/70" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="flex gap-5">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/40 dark:to-purple-900/40 rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {viewingProfile.logoUrl ? (
                    <img src={viewingProfile.logoUrl} alt={viewingProfile.name} className="w-full h-full object-cover rounded-2xl" />
                  ) : (
                    <span className="text-4xl">{icon}</span>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <h4 className="text-xl font-bold dark:text-white">{viewingProfile.name}</h4>
                    <CheckCircle className="w-4 h-4 text-blue-500" />
                  </div>
                  {viewingProfile.businessTypes && viewingProfile.businessTypes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {viewingProfile.businessTypes.map((t, i) => (
                        <span key={i} className="px-2.5 py-0.5 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded-lg text-xs capitalize">
                          {t.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                  <span className="inline-block px-2.5 py-0.5 rounded-full text-xs text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-400">
                    Available
                  </span>
                </div>
              </div>

              {viewingProfile.description && (
                <div>
                  <h5 className="font-semibold text-sm mb-1.5 dark:text-white">About</h5>
                  <p className="text-sm text-gray-600 dark:text-white/70">{viewingProfile.description}</p>
                </div>
              )}

              <div>
                <h5 className="font-semibold text-sm mb-2 dark:text-white">Contact & Location</h5>
                <div className="space-y-2">
                  {viewingProfile.address?.city && (
                    <div className="flex items-center gap-3 text-sm dark:text-white/70">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span>{[viewingProfile.address.street, viewingProfile.address.city, viewingProfile.address.zipCode, viewingProfile.address.country].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                  {viewingProfile.phone && (
                    <div className="flex items-center gap-3 text-sm dark:text-white/70">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span>{viewingProfile.phone}</span>
                    </div>
                  )}
                  {viewingProfile.email && (
                    <div className="flex items-center gap-3 text-sm dark:text-white/70">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span>{viewingProfile.email}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Interactive map for this business location */}
              {viewingProfile.address?.city && geocodedPositions[viewingProfile.id] && (
                <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-white/10" style={{ height: 200 }}>
                  <LeafletMap
                    positions={{ [viewingProfile.id]: geocodedPositions[viewingProfile.id] }}
                    businesses={[viewingProfile]}
                    onBookNow={handleBookNow}
                    getLocationString={getBusinessLocationString}
                  />
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 dark:bg-[#0F0E16] border-t border-gray-200 dark:border-white/8 px-6 py-4 flex gap-3">
              <button
                onClick={() => setViewingProfile(null)}
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 dark:text-white text-sm"
              >
                Close
              </button>
              <button
                onClick={() => { setViewingProfile(null); handleBookNow(viewingProfile); }}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold"
              >
                Book Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
